import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldAlert, UserMinus, EyeOff, Flag } from 'lucide-react';

const REASONS = [
  { value: 'harassment',    label: 'Harassment or bullying' },
  { value: 'threats',       label: 'Threats or intimidation' },
  { value: 'inappropriate', label: 'Inappropriate or explicit content' },
  { value: 'spam',          label: 'Spam or solicitation' },
  { value: 'other',         label: 'Something else' },
];

const ACTIONS = [
  {
    value: 'report', icon: Flag, title: 'Report only',
    detail: 'We review it. You stay connected and they are not told you reported them.',
  },
  {
    value: 'disconnect', icon: UserMinus, title: 'Report and disconnect',
    detail: 'Removes the connection, so they cannot message you while we review.',
  },
  {
    value: 'block', icon: EyeOff, title: 'Report and block',
    detail: 'You become invisible to them across the platform — messages, connections, member search and the forum.',
  },
];

/**
 * Reporting a private conversation. The member chooses exactly which messages the
 * admins may read, so nobody hands over their whole private history to make a report.
 */
export const ReportConversationDialog = ({
  open, onOpenChange, conversationId, otherName, selectedIds, onReported,
}) => {
  const [reason, setReason] = useState('harassment');
  const [note, setNote] = useState('');
  const [action, setAction] = useState('report');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (note.trim().length < 10) {
      toast.error('Please tell us briefly what is happening');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/messages/dm/${conversationId}/report`, {
        message_ids: selectedIds,
        reason,
        description: note.trim(),
        action,
      });
      toast.success(
        r.data.blocked ? 'Reported. You are now invisible to them.'
          : r.data.disconnected ? 'Reported and disconnected.'
            : 'Report submitted. Our team will review it.',
      );
      setNote('');
      setAction('report');
      onOpenChange(false);
      onReported?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit the report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dm-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-orange-500" />
            Report {otherName || 'this member'}
          </DialogTitle>
          <DialogDescription>
            You have selected {selectedIds.length} message{selectedIds.length === 1 ? '' : 's'}.
            Only those will be shared with the admins — not the rest of your conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">What is happening?</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="dm-report-reason"
            >
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">Tell us more</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Explain what has been going on, and anything the admins should know."
              data-testid="dm-report-note"
            />
            <p className="text-xs text-gray-500 mt-1">
              This note goes to the admins only. The member is never shown who reported them.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium block mb-2">What would you like to happen?</label>
            <div className="space-y-2">
              {ACTIONS.map((a) => {
                const Icon = a.icon;
                const active = action === a.value;
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => setAction(a.value)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                      active ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`dm-report-action-${a.value}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 ${active ? 'text-cyan-600' : 'text-gray-400'}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{a.title}</span>
                      <span className="block text-xs text-gray-500 leading-relaxed">{a.detail}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dm-report-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || note.trim().length < 10}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="dm-report-submit"
            >
              {submitting ? 'Sending…' : 'Submit report'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportConversationDialog;
